import React, { useCallback, useEffect, useRef, useState } from 'react';
import { runCatalogShare, runCatalogShareArticle, buildShareCatalogUrl } from '../../utils/catalogShare';
import './BrowserCatalogShareButton.css';

export default function BrowserCatalogShareButton({ itemId, title }) {
  const [hint, setHint] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDocDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocDown, true);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onDocDown, true);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [menuOpen]);

  const runShareLink = useCallback(async () => {
    const id = Number(itemId);
    if (!Number.isFinite(id) || id <= 0) return;
    const shareUrl = buildShareCatalogUrl(id);
    try {
      const result = await runCatalogShare({ shareUrl, title });
      setMenuOpen(false);
      if (result === 'copied') {
        setHint('Скопировано');
        setTimeout(() => setHint(null), 2000);
      } else {
        setHint(null);
      }
    } catch {
      setMenuOpen(false);
      setHint('Ошибка');
      setTimeout(() => setHint(null), 2000);
    }
  }, [itemId, title]);

  const runShareArticle = useCallback(async () => {
    const id = Number(itemId);
    if (!Number.isFinite(id) || id <= 0) return;
    try {
      const result = await runCatalogShareArticle({ articleText: String(id), title });
      setMenuOpen(false);
      if (result === 'copied') {
        setHint('Артикул скопирован');
        setTimeout(() => setHint(null), 2000);
      } else {
        setHint(null);
      }
    } catch {
      setMenuOpen(false);
      setHint('Ошибка');
      setTimeout(() => setHint(null), 2000);
    }
  }, [itemId, title]);

  const toggleMenu = useCallback((e) => {
    e.stopPropagation();
    setMenuOpen((v) => !v);
  }, []);

  const hintIsCopied = hint === 'Скопировано' || hint === 'Артикул скопирован';

  return (
    <div className="browser-catalog-share-wrap" ref={wrapRef}>
      <button
        type="button"
        className="browser-catalog-share"
        onClick={toggleMenu}
        aria-expanded={menuOpen}
        aria-haspopup="menu"
        aria-label={hintIsCopied ? hint : 'Поделиться'}
      >
        <svg className="browser-catalog-share__icon" viewBox="0 0 24 24" width="24" height="24" aria-hidden>
          <path
            fill="currentColor"
            d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z"
          />
        </svg>
      </button>
      {menuOpen ? (
        <div className="browser-catalog-share__menu" role="menu" aria-label="Способ поделиться">
          <button
            type="button"
            className="browser-catalog-share__menu-item"
            role="menuitem"
            onClick={(e) => {
              e.stopPropagation();
              runShareLink();
            }}
          >
            Поделиться ссылкой
          </button>
          <button
            type="button"
            className="browser-catalog-share__menu-item"
            role="menuitem"
            onClick={(e) => {
              e.stopPropagation();
              runShareArticle();
            }}
          >
            Поделиться артикулом
          </button>
        </div>
      ) : null}
      {hint ? <span className="browser-catalog-share__hint">{hint}</span> : null}
    </div>
  );
}
